import { CollectDataStepType } from 'src/constants/collect-data/enums'
import type { InsurancePolicyProduct } from 'src/interfaces/insurancePolicy'
import type {
  InsuranceStepConfig,
  OnboardingTemplate,
  OnboardingTemplateStep,
} from 'src/interfaces/onboardingTemplate'
import { normalizeCollectDataTemplateConfigId, resolveStepGroupConfigId } from './configIds'

/**
 * Stable prefix for insurance coverage rows (`insurance_${insuranceProductId}`).
 * Use {@link mintInsuranceCoverageStepLabel} when creating new editor/API rows.
 */
export function insuranceCoverageStepLabelForProduct(insuranceProductId: string): string {
  return `insurance_${String(insuranceProductId).trim()}`
}

/** Unique `collectDataTemplateStepLabel` / step `label` for each new insurance coverage row. */
export function mintInsuranceCoverageStepLabel(insuranceProductId: string): string {
  return `${insuranceCoverageStepLabelForProduct(insuranceProductId)}`
}

/** Drops duplicate `insuranceProductId` values (API quirks); skips blank ids. */
export function dedupeInsurancePolicyProductsForSync<
  T extends Pick<InsurancePolicyProduct, 'id' | 'insuranceProductId'>,
>(products: T[]): T[] {
  const seen = new Set<string>()
  const dedupedProducts: T[] = []

  for (const policyProduct of products) {
    const key = String(policyProduct.insuranceProductId ?? '').trim()

    if (key === '') {
      continue
    }

    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    dedupedProducts.push(policyProduct)
  }

  return dedupedProducts
}

export function getFlowEditorGroupSteps(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
): OnboardingTemplateStep[] {
  const normalizedConfigId = normalizeCollectDataTemplateConfigId(configId) || configId

  return editorSteps.filter((step) => {
    const groupConfigId = resolveStepGroupConfigId(step, template)
    const normalizedGroupConfigId =
      normalizeCollectDataTemplateConfigId(groupConfigId) || groupConfigId

    return normalizedGroupConfigId === normalizedConfigId
  })
}

export function flowEditorGroupHasInsuranceCoverageSteps(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
): boolean {
  return getFlowEditorGroupSteps(editorSteps, template, configId).some(
    (step) => step.type === CollectDataStepType.InsuranceCoverage,
  )
}

export function flowEditorGroupHasNonInsuranceSteps(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
): boolean {
  return getFlowEditorGroupSteps(editorSteps, template, configId).some(
    (step) => step.type !== CollectDataStepType.InsuranceCoverage,
  )
}

/** True when the group is linked to a policy and already has synced insurance coverage rows. */
export function flowEditorGroupAddStepDisabled(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
  insurancePolicyId: string | null | undefined,
): boolean {
  const hasPolicy =
    insurancePolicyId !== null &&
    insurancePolicyId !== undefined &&
    String(insurancePolicyId).trim() !== ''

  if (!hasPolicy) {
    return false
  }

  return flowEditorGroupHasInsuranceCoverageSteps(editorSteps, template, configId)
}

/** True when the group contains step types other than insurance coverage (sync would mix groups). */
export function flowEditorGroupInsuranceSyncDisabled(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
): boolean {
  return flowEditorGroupHasNonInsuranceSteps(editorSteps, template, configId)
}

function normalizeInsurancePolicyIdForCompare(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null
  }

  const trimmed = value.trim()

  return trimmed === '' ? null : trimmed
}

/** Policy id last written onto insurance coverage rows (unchanged until the user syncs). */
export function getCommittedInsurancePolicyIdForGroup(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
): string | null {
  const groupSteps = getFlowEditorGroupSteps(editorSteps, template, configId)
  const firstInsurance = groupSteps.find(
    (step) => step.type === CollectDataStepType.InsuranceCoverage,
  )

  if (!firstInsurance) {
    return null
  }

  const insuranceConfig = firstInsurance.config as InsuranceStepConfig
  const policyId = insuranceConfig.insurancePolicyId

  return normalizeInsurancePolicyIdForCompare(policyId)
}

/**
 * On sync: when the group already has insurance coverage rows and the selected policy differs
 * from the policy on those rows, replace the whole steps config (new id + name) instead of
 * updating steps in place.
 */
export function shouldRecreateFlowEditorInsuranceGroupOnSync(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  configId: string,
  targetPolicyId: string | null | undefined,
): boolean {
  const committed = getCommittedInsurancePolicyIdForGroup(editorSteps, template, configId)
  const target = normalizeInsurancePolicyIdForCompare(targetPolicyId)

  if (committed === null) {
    return false
  }

  if (target === null) {
    return false
  }

  if (committed === target) {
    return false
  }

  return flowEditorGroupHasInsuranceCoverageSteps(editorSteps, template, configId)
}

/**
 * When non-null, sync must not run: policy would create insurance coverage rows whose labels
 * already exist on another step group (backend rejects duplicate type + label).
 */
export function getInsuranceSyncBlockedByExistingStepsMessage(
  editorSteps: OnboardingTemplateStep[],
  template: OnboardingTemplate,
  targetConfigId: string,
  products: Pick<InsurancePolicyProduct, 'id' | 'insuranceProductId'>[],
): string | null {
  const uniqueProducts = dedupeInsurancePolicyProductsForSync(products)

  if (uniqueProducts.length === 0) {
    return null
  }

  const targetKey = normalizeCollectDataTemplateConfigId(targetConfigId) || targetConfigId
  const conflictingLabels: string[] = []

  for (const policyProduct of uniqueProducts) {
    const productKey = String(policyProduct.insuranceProductId ?? '').trim()

    if (productKey === '') {
      continue
    }

    const labelPrefix = insuranceCoverageStepLabelForProduct(productKey)

    const existsInOtherGroup = editorSteps.some((step) => {
      if (step.type !== CollectDataStepType.InsuranceCoverage) {
        return false
      }

      const insuranceConfig = step.config as InsuranceStepConfig
      const stepProductId =
        typeof insuranceConfig.insurancePolicyProductId === 'string'
          ? insuranceConfig.insurancePolicyProductId.trim()
          : ''

      if (stepProductId !== '' && stepProductId === policyProduct.id) {
        const groupConfigId = resolveStepGroupConfigId(step, template)
        const normalizedGroupConfigId =
          normalizeCollectDataTemplateConfigId(groupConfigId) || groupConfigId

        return normalizedGroupConfigId !== targetKey
      }

      const stepLabel = String(step.collectDataTemplateStepLabel ?? '').trim()

      if (stepLabel !== labelPrefix && !stepLabel.startsWith(`${labelPrefix}_`)) {
        return false
      }

      const groupConfigId = resolveStepGroupConfigId(step, template)
      const normalizedGroupConfigId =
        normalizeCollectDataTemplateConfigId(groupConfigId) || groupConfigId

      return normalizedGroupConfigId !== targetKey
    })

    if (existsInOtherGroup) {
      conflictingLabels.push(labelPrefix)
    }
  }

  if (conflictingLabels.length === 0) {
    return null
  }

  if (conflictingLabels.length === 1) {
    return `Cannot sync: an insurance coverage step with label "${conflictingLabels[0]}" already exists in another group. Remove it first.`
  }

  return `Cannot sync: insurance coverage steps already exist in other groups (${conflictingLabels.join(
    ', ',
  )}). Remove those steps first.`
}
